import React, { useContext, useEffect, useMemo, useState } from 'react'
import { Button, Container, Grid, makeStyles } from '@material-ui/core'
import { withStyles } from '@material-ui/core/styles'
import HelpRounded from '@material-ui/icons/HelpRounded'
import Tooltip from '@material-ui/core/Tooltip'
import { isSafari } from 'react-device-detect'

import { isEmpty } from 'lodash'
import * as d3 from 'd3'
import behaviourChartConst from '../data/behaviorAnalysisChart'
import PageSection from '../components/PageSection'
import AreaChart from '../components/AreaChart'
import ProjectionChart from '../components/ProjectionChart'
import ChartDialog from '../components/ChartDialog'
import expandIcon from '../assets/images/expandNew.svg'
import { Context as PortfolioContext } from '../contexts/portfolio'
import RadioToggle from '../components/RadioToggle'
import ChartTooltipModal from '../components/ChartTooltipModal'

const useStyles = makeStyles({
  subheading: { marginBottom: '10px' },
  chart: {
    marginTop: '16px',
    padding: '0px !important',
    flexBasis: 'unset',
    width: 'calc(100% / 3 - 10px)',
  },
  areaChart: {
    marginTop: 20,
  },
  lineChart: {
    marginBottom: 30,
  },
  marketContextContainer: {
    background: '#fff',
    maxWidth: '100%',
    paddingLeft: 0,
    paddingRight: 0,
    marginBottom: -20,
    marginTop: 15,
  },
  buttonGroup: {
    display: 'flex',
    alignItems: 'center',
    fontSize: 12,
    '& > div': {
      backgroundColor: '#F3F3F3',
      padding: '8px 12px',
      marginRight: 10,
      borderRadius: 20,
    },
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: 12,
    marginBottom: 20,
    marginLeft: 10,
    lineHeight: '18px',
    fontWeight: 400,
    '& > div': {
      display: 'flex',
      alignItems: 'center',
      marginRight: 16,
      '& div': {
        marginRight: 8,
        width: 10,
        height: 10,
        borderRadius: '50%',
      },
    },
  },
  customGrid: {
    marginBottom: 10,
  },
  headChartSection: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
    width: '100%',
  },
  titleCombo: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    width: '100%',
    paddingLeft: '10px',
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  titleText: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 600,
    color: '#181818',
  },
  titleTextInModal: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 24,
    lineHeight: '28px',
    fontWeight: 600,
    color: '#181818',
  },
  icon: {
    display: 'flex',
    marginTop: 8,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#757575',
    '& svg': {
      width: 14,
    },
  },
  radioToggle: {},
  expandIcon: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
    color: '#1B3963',
    background: '#EDF2F7',
    borderRadius: 4,
    width: 32,
    height: 32,
    '& img': {
      width: 32,
      height: 32,
    },
    '& svg': {
      width: 32,
    },
  },
  chartChild: {
    width: '100%',
    height: '90%',
  },
  chartHeader: {
    color: '#474747',
    marginBottom: '7px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headIntro: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  subTitle: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    lineHeight: '18px',
    marginLeft: 2,
    fontWeight: 400,
    color: '#757575',
  },
  subTitleInModal: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 20,
    lineHeight: '24px',
    marginLeft: 2,
    fontWeight: 400,
    color: '#757575',
  },
  toolTip: {
    color: '#8D9CB1',
    width: 16,
    height: 16,
    fontSize: 16,
    cursor: 'pointer',
    verticalAlign: 'middle',
    marginLeft: 2,
  },
  toolTipInModal: {
    color: '#8D9CB1',
    width: 20,
    height: 20,
    fontSize: 20,
    cursor: 'pointer',
    verticalAlign: 'middle',
    marginLeft: 10,
  },
})

const CustomToolTip = withStyles({
  tooltip: {
    backgroundColor: 'transparent !important',
    boxShadow: 'unset !important',
  },
})(Tooltip)

const ESTIMATE_INDEX_TOGGLE = [
  { id: 0, value: 'Annual' },
  { id: 2, value: 'Interim' },
]

const BehaviouralAnalysis = ({ tickerId }) => {
  const classes = useStyles()
  const { state } = useContext(PortfolioContext)
  const [isOpenExpandModal, setOpenExpandModal] = useState(false)
  const [chartDetail, setChartDetail] = React.useState({})
  const [behaviourChart, setBehaviourChart] = useState(behaviourChartConst)
  const [toggleEstimateIndexChart, setToggleEstimateIndexChart] = useState(
    ESTIMATE_INDEX_TOGGLE[0],
  )
  const [frequency, setFrequency] = useState('annual')
  const [openTooltip, setOpenTooltip] = React.useState(false)
  const [tooltipInfo, setTooltipInfo] = React.useState({})
  const [showAnnually, setShowAnnually] = useState(true)
  const handleClickOpenTooltip = chart => {
    setTooltipInfo(chart?.tooltipInfo)
    setOpenTooltip(true)
  }

  useEffect(() => {
    setBehaviourChart(charts =>
      ['estimates', 'highConvictions', 'bondInvestors'].map((k, i) => ({
        ...charts[i],
        data: state.behaviorData[k],
      })),
    )
    if (
      state.behaviorData['estimates']?.filter(d => d.frequency !== 'annual')
        .length > 0 &&
      state.behaviorData['estimates']?.filter(d => d.frequency === 'annual')
        .length === 0
    ) {
      onToggleEstimateIndexChart({ id: 2, value: 'Interim' })
    }
  }, [state.behaviorData])

  const onZoom = chartDetail => {
    setOpenExpandModal(true)
    setChartDetail(chartDetail)
  }

  const onToggleEstimateIndexChart = obj => {
    setToggleEstimateIndexChart(obj)
    setFrequency(obj.value.toLowerCase())
    setShowAnnually(obj.value.toLowerCase() === 'annual')
  }

  const showingToggle =
    state.behaviorData['estimates']?.filter(d => d.frequency !== 'annual')
      .length > 0 &&
    state.behaviorData['estimates']?.filter(d => d.frequency === 'annual')
      .length > 0

  const chartDiv = useMemo(
    () => (chart, inModal, estimateChartFrequency, showAnnually) => {
      return !inModal ? (
        chart?.data?.length > 1 && (
          <Grid
            key={chart.title}
            className={classes.chart}
            item
            xs={4}
            style={{
              height:
                chart.type === 'ProjectionChart' && showingToggle
                  ? '200px'
                  : '220px',
            }}
          >
            <ChartSection
              title={chart.title}
              subTitle={chart.subTitle}
              chartDetail={chart}
              showAnnually={showAnnually}
            >
              {chart.type === 'ProjectionChart' ? (
                <ProjectionChart
                  estimateChartFrequency={estimateChartFrequency}
                  inModal={inModal}
                  data={chart.data}
                />
              ) : (
                <AreaChart
                  inModal={inModal}
                  data={chart.data}
                  yKey={[chart.yKey]}
                />
              )}
            </ChartSection>
          </Grid>
        )
      ) : (
        <ChartSection
          title={chart.title}
          subTitle={chart.subTitle}
          inModal={inModal}
        >
          {chart.type === 'ProjectionChart' ? (
            <ProjectionChart
              estimateChartFrequency={estimateChartFrequency}
              inModal={inModal}
              data={chart.data}
            />
          ) : (
            <AreaChart
              inModal={inModal}
              data={chart.data}
              yKey={[chart.yKey]}
            />
          )}
        </ChartSection>
      )
    },
    [state.behaviorData],
  )

  const ChartSection = ({
    title,
    subTitle,
    inModal,
    children,
    chartDetail,
    showAnnually,
  }) => {
    return (
      <>
        <div className={classes.headChartSection}>
          <div className={classes.headIntro}>
            <div className={classes.titleCombo}>
              <div className={classes.titleRow}>
                <span
                  className={
                    inModal ? classes.titleTextInModal : classes.titleText
                  }
                >
                  {title}
                </span>
                <HelpRounded
                  className={
                    inModal ? classes.titleTextInModal : classes.toolTip
                  }
                  onClick={() => handleClickOpenTooltip(chartDetail)}
                />
                <span
                  className={
                    inModal ? classes.subTitleInModal : classes.subTitle
                  }
                >
                  ({subTitle})
                </span>
              </div>
              <div className={classes.icon}>
                {title === 'Forward Looking EPS Forecast' &&
                  showingToggle &&
                  state.behaviorData['estimates'].length > 0 && (
                    <div className={classes.radioToggle}>
                      <RadioToggle
                        list={ESTIMATE_INDEX_TOGGLE}
                        getActiveValue={value =>
                          onToggleEstimateIndexChart(value)
                        }
                        activeItemDefault={
                          showAnnually
                            ? ESTIMATE_INDEX_TOGGLE[0]
                            : ESTIMATE_INDEX_TOGGLE[1]
                        }
                      />
                    </div>
                  )}
              </div>
            </div>
            {!inModal && !isSafari && (
              <div
                onClick={() => onZoom(chartDetail)}
                className={`${classes.expandIcon} tooltip`}
                // the id indicates this element is ignored when generating pdf
                id={`expandBehaviorAnalysisChartBtn-${title}`}
              >
                <img src={expandIcon} />
              </div>
            )}
          </div>
        </div>
        <div className={classes.chartChild}>{children}</div>
      </>
    )
  }

  if (!behaviourChart) {
    return null
  }

  return (
    <>
      <PageSection
        title="Behavioural Analysis"
        subTitle="Street Perception & Investor Confidence Measures"
        behaviouralToolTip
      >
        <ChartDialog
          chart={chartDiv(chartDetail, true, frequency, showAnnually)}
          isOpenExpandModal={isOpenExpandModal}
          setOpenExpandModal={value => {
            d3.selectAll('.projectionChart-inModal').remove()
            setOpenExpandModal(value)
          }}
        />
        <Container className={classes.marketContextContainer}>
          <Grid container spacing={3} className={classes.customGrid}>
            {behaviourChart.map(chart =>
              chartDiv(chart, false, frequency, showAnnually),
            )}
          </Grid>
          {behaviourChart[0].data.length !== 0 && (
            <div className={classes.legend}>
              <div>
                <div style={{ backgroundColor: '#1D82F9' }} />
                Smart Consensus
              </div>
              <div>
                <div style={{ backgroundColor: '#34D8E2' }} />
                Consensus
              </div>
            </div>
          )}
        </Container>
      </PageSection>
      <ChartTooltipModal
        open={openTooltip}
        handleClose={() => {
          setOpenTooltip(false)
        }}
        tooltipInfo={tooltipInfo}
      />
    </>
  )
}

export default React.memo(BehaviouralAnalysis)
